import { useEffect, useRef, useState } from 'react';
import { Button } from '@tark/shared/components';
import { apiClient } from '@tark/shared/lib';
import type { ApiRequestConfig } from '@tark/shared/lib/api';

export default function InviteVerification({ channel, expectedUserId, onChecked }: { channel: 'email' | 'phone'; expectedUserId: string; onChecked: () => Promise<void> }) {
  const mounted = useRef(true);
  const route = useRef(window.location.pathname);
  const options: ApiRequestConfig = { handleAccountSetup: true,
    headers: { 'X-TARK-Expected-User-Id': expectedUserId },
    assertRequestCurrent: () => {
      if (!mounted.current || window.location.pathname !== route.current) throw new Error('Verification account or route changed');
    },
  };
  const flight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState(0);
  const [retryAt, setRetryAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    mounted.current = true;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => { mounted.current = false; window.clearInterval(timer); };
  }, []);
  const run = async (work: () => Promise<void>, confirming = false) => {
    if (flight.current || !mounted.current) return;
    flight.current = true; setBusy(true);
    try { await work(); }
    catch (error) {
      if (!mounted.current) return;
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 410) { const expiredAt = Date.now(); setNow(expiredAt); setExpiresAt(expiredAt); setNotice('This code expired. Request a new code.'); }
      else if (status === 429) setNotice('Too many attempts. Wait before trying again.');
      else if (status === 400 && confirming) setNotice('That code did not match. Check the latest code and try again.');
      else setNotice('Could not confirm the result. Check your messages or try checking status again.');
    } finally { flight.current = false; if (mounted.current) setBusy(false); }
  };
  const check = async () => {
    const { data } = await apiClient.get('/api/v1/web/user/profile', options);
    if (!mounted.current) return;
    const verified = channel === 'email' ? data?.emailVerified : data?.mobileVerified;
    if (verified === true) { setNotice('Verified. Preview the invitation to continue.'); await onChecked(); }
    else setNotice(verified === false ? 'Not verified yet. Use the latest verification message, then check again.' : 'Verification status is unavailable. Try again.');
  };
  const send = () => run(async () => {
    if (Date.now() < retryAt) return;
    const requestedAt = Date.now();
    setNow(requestedAt); setRetryAt(requestedAt + 60000); setNotice('');
    if (channel === 'email') {
      const { data } = await apiClient.post('/api/v1/web/user/resend-verification', undefined, options);
      if (!mounted.current) return;
      if (data?.status === 'already_verified') await check();
      else setNotice('Check your inbox for the verification link, then return here.');
    } else {
      setRequested(true);
      setExpiresAt(0);
      const { data } = await apiClient.post('/api/v1/web/user/changes/verify-phone/request', undefined, options);
      if (!mounted.current) return;
      const ttl = typeof data?.expiresIn === 'number' && data.expiresIn > 0 && data.expiresIn <= 600 ? data.expiresIn : 600;
      setExpiresAt(Date.now() + ttl * 1000); setCode('');
      setNotice('Enter the latest code from your messages.');
    }
  });
  const confirm = () => run(async () => {
    if (!/^\d{6}$/.test(code) || (expiresAt > 0 && Date.now() >= expiresAt)) return;
    await apiClient.post('/api/v1/web/user/changes/verify-phone/confirm', { verificationCode: code }, options);
    if (!mounted.current) return;
    setCode(''); await check();
  }, true);
  const remaining = Math.max(0, Math.ceil((retryAt - now) / 1000));
  const expired = expiresAt > 0 && now >= expiresAt;
  const displayedNotice = expired ? 'This code expired. Request a new code.' : notice;
  return <div className="mt-4 space-y-3 border-t border-border pt-4">
    <h2 className="text-lg font-semibold">Verify your {channel}</h2>
    <div className="flex flex-wrap gap-3">
      <Button onClick={send} disabled={busy || remaining > 0}>{remaining ? `Resend in ${remaining}s` : channel === 'email' ? 'Send verification email' : 'Send code'}</Button>
      <Button variant="outline" onClick={() => void run(check)} disabled={busy}>Check status</Button>
    </div>
    {channel === 'phone' && requested && <form className="flex flex-wrap items-end gap-3" onSubmit={event => { event.preventDefault(); void confirm(); }}>
      <label className="block min-w-0">Verification code
        <input className="mt-1 block w-full rounded-lg border border-border bg-surface p-3 text-text" value={code}
          onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          autoComplete="one-time-code" inputMode="numeric" maxLength={6} disabled={busy} />
      </label>
      <Button type="submit" disabled={busy || code.length !== 6 || expired}>Verify code</Button>
    </form>}
    {displayedNotice && <p role="status" className="text-sm text-text-muted">{displayedNotice}</p>}
  </div>;
}
